import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiX, FiUpload, FiDownload, FiCheckCircle, FiAlertTriangle, FiFileText, FiInfo, FiBookOpen, FiLayers } = FiIcons;

const ExcelUploadModal = ({ isOpen, onClose, onUpload }) => {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [activeTab, setActiveTab] = useState('upload');

  const requiredColumns = [
    {
      name: 'text',
      description: 'The question text/statement',
      example: 'What is the capital of France?'
    },
    {
      name: 'type',
      description: 'Question type',
      example: 'Multiple Choice, Short Answer, Essay, True/False, Fill in the Blank, Problem Solving'
    },
    {
      name: 'subject',
      description: 'Subject area',
      example: 'Mathematics, Science, History, etc.'
    },
    {
      name: 'courseCode',
      description: 'Course code for proper tagging',
      example: 'LLE3012, CS101, MATH201, etc.'
    },
    {
      name: 'difficulty',
      description: 'Difficulty level',
      example: 'Easy, Medium, Hard'
    },
    {
      name: 'bloomLevel',
      description: 'Bloom\'s Taxonomy level',
      example: 'knowledge, comprehension, application, analysis, synthesis, evaluation'
    },
    {
      name: 'vocabularyTier',
      description: 'Vocabulary complexity tier',
      example: 'A1, A2, B1, B2, C1, C2'
    },
    {
      name: 'cefrLevel',
      description: 'CEFR language proficiency level',
      example: 'A1, A2, B1, B2, C1, C2'
    },
    {
      name: 'marks',
      description: 'Points/marks for the question',
      example: '1, 2, 5, 10, etc.'
    }
  ];

  const optionalColumns = [
    {
      name: 'options',
      description: 'Answer options for Multiple Choice (comma-separated)',
      example: 'Paris, London, Berlin, Madrid'
    },
    {
      name: 'correctAnswer',
      description: 'Correct answer for Multiple Choice',
      example: 'Paris'
    },
    {
      name: 'explanation',
      description: 'Additional explanation or notes',
      example: 'Paris is the capital and largest city of France.'
    }
  ];

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      previewExcelFile(selectedFile);
    }
  };

  const previewExcelFile = async (file) => {
    try {
      const XLSX = await import('xlsx');
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          setPreviewData(jsonData.slice(0, 3));
        } catch (error) {
          console.error('Error reading Excel file:', error);
          alert('Error reading Excel file. Please check the file format.');
        }
      };
      
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error loading XLSX library:', error);
      alert('Error loading Excel processing library. Please try again.');
    }
  };

  const parseCSVContent = (csvContent) => {
    const lines = csvContent.split('\n');
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        data.push(row);
      }
    }

    return data;
  };

  const validateData = (data) => {
    const errors = [];
    const validRows = [];

    data.forEach((row, index) => {
      const rowErrors = [];

      // Check required columns
      requiredColumns.forEach(col => {
        if (!row[col.name] || row[col.name].toString().trim() === '') {
          rowErrors.push(`Missing ${col.name}`);
        }
      });

      // Validate specific fields
      if (row.type && !['Multiple Choice', 'Short Answer', 'Essay', 'True/False', 'Fill in the Blank', 'Problem Solving'].includes(row.type)) {
        rowErrors.push('Invalid question type');
      }

      if (row.difficulty && !['Easy', 'Medium', 'Hard'].includes(row.difficulty)) {
        rowErrors.push('Invalid difficulty level');
      }

      if (row.bloomLevel && !['knowledge', 'comprehension', 'application', 'analysis', 'synthesis', 'evaluation'].includes(row.bloomLevel)) {
        rowErrors.push('Invalid Bloom\'s taxonomy level');
      }

      if (row.vocabularyTier && !['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].includes(row.vocabularyTier)) {
        rowErrors.push('Invalid vocabulary tier');
      }
      
      if (row.cefrLevel && !['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].includes(row.cefrLevel)) {
        rowErrors.push('Invalid CEFR level');
      }

      if (row.marks && (isNaN(row.marks) || row.marks < 1)) {
        rowErrors.push('Invalid marks (must be number ≥ 1)');
      }

      // Validate course code format (basic validation)
      if (row.courseCode && !/^[A-Z]{2,4}\d{3,4}$/i.test(row.courseCode.toString().trim())) {
        rowErrors.push('Invalid course code format (should be like CS101, MATH2001, etc.)');
      }

      // For multiple choice questions, validate options and correct answer
      if (row.type === 'Multiple Choice') {
        if (!row.options || !row.correctAnswer) {
          rowErrors.push('Multiple choice questions require options and correct answer');
        } else {
          const options = row.options.toString().split(',').map(opt => opt.trim());
          if (options.length < 2) {
            rowErrors.push('Multiple choice questions need at least 2 options');
          }
          if (!options.includes(row.correctAnswer.toString().trim())) {
            rowErrors.push('Correct answer must be one of the options');
          }
        }
      }

      if (rowErrors.length > 0) {
        errors.push({ row: index + 2, errors: rowErrors });
      } else {
        const processedRow = {
          text: row.text.toString().trim(),
          type: row.type,
          subject: row.subject.toString().trim(),
          courseCode: row.courseCode.toString().toUpperCase().trim(),
          difficulty: row.difficulty,
          bloomLevel: row.bloomLevel,
          vocabularyTier: row.vocabularyTier,
          cefrLevel: row.cefrLevel,
          marks: parseInt(row.marks),
          explanation: row.explanation?.toString().trim() || ''
        };

        if (row.type === 'Multiple Choice' && row.options) {
          processedRow.options = row.options.toString().split(',').map(opt => opt.trim()).filter(opt => opt);
          processedRow.correctAnswer = row.correctAnswer.toString().trim();
        }

        validRows.push(processedRow);
      }
    });

    return { errors, validRows };
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadResults(null);

    try {
      let jsonData = [];

      if (file.name.endsWith('.csv')) {
        const text = await file.text();
        jsonData = parseCSVContent(text);
      } else {
        const XLSX = await import('xlsx');
        const reader = new FileReader();
        
        reader.onload = async (e) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          jsonData = XLSX.utils.sheet_to_json(worksheet);

          const { errors, validRows } = validateData(jsonData);

          if (validRows.length > 0) {
            await onUpload(validRows);
          }

          setUploadResults({
            total: jsonData.length,
            successful: validRows.length,
            failed: errors.length,
            errors: errors
          });
        };
        
        reader.readAsArrayBuffer(file);
        return;
      }

      const { errors, validRows } = validateData(jsonData);

      if (validRows.length > 0) {
        await onUpload(validRows);
      }

      setUploadResults({
        total: jsonData.length,
        successful: validRows.length,
        failed: errors.length,
        errors: errors
      });
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error processing file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const downloadTemplate = async () => {
    const templateData = [
      {
        text: 'What is the capital of France?',
        type: 'Multiple Choice',
        subject: 'Geography',
        courseCode: 'GEO101',
        difficulty: 'Easy',
        bloomLevel: 'knowledge',
        vocabularyTier: 'A1',
        cefrLevel: 'A1',
        marks: 1,
        options: 'Paris, London, Berlin, Madrid',
        correctAnswer: 'Paris',
        explanation: 'Paris is the capital and largest city of France.'
      },
      {
        text: 'Explain the process of photosynthesis.',
        type: 'Short Answer',
        subject: 'Biology',
        courseCode: 'BIO101',
        difficulty: 'Medium',
        bloomLevel: 'comprehension',
        vocabularyTier: 'B1',
        cefrLevel: 'B1',
        marks: 5,
        options: '',
        correctAnswer: '',
        explanation: 'Students should explain how plants convert light energy into chemical energy.'
      },
      {
        text: 'Analyze the causes of World War I.',
        type: 'Essay',
        subject: 'History',
        courseCode: 'HIS301',
        difficulty: 'Hard',
        bloomLevel: 'analysis',
        vocabularyTier: 'B2',
        cefrLevel: 'C1',
        marks: 15,
        options: '',
        correctAnswer: '',
        explanation: 'Students should analyze multiple factors including imperialism, alliances, nationalism, and the immediate trigger.'
      }
    ];

    try {
      const XLSX = await import('xlsx');
      const worksheet = XLSX.utils.json_to_sheet(templateData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Questions Template');
      XLSX.writeFile(workbook, 'questions_template.xlsx');
    } catch (error) {
      console.warn('Excel export failed, falling back to CSV');
      const csvContent = [
        Object.keys(templateData[0]).join(','),
        ...templateData.map(row => Object.values(row).map(val => `"${val}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'questions_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreviewData(null);
    setUploadResults(null);
    setActiveTab('upload');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Upload Questions from Excel/CSV</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Import multiple questions at once from an Excel or CSV file
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setActiveTab('upload')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'upload'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <SafeIcon icon={FiUpload} className="inline mr-2" />
                  Upload File
                </button>
                <button
                  onClick={() => setActiveTab('instructions')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'instructions'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <SafeIcon icon={FiBookOpen} className="inline mr-2" />
                  Format Instructions
                </button>
              </nav>
            </div>

            <div className="p-6">
              {activeTab === 'upload' ? (
                <div className="space-y-6">
                  {/* Template Download */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-blue-800 mb-1">Download Template</h4>
                        <p className="text-sm text-blue-600">
                          Download our template to see the required format with Course Code and CEFR Level
                        </p>
                      </div>
                      <button
                        onClick={downloadTemplate}
                        className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <SafeIcon icon={FiDownload} />
                        <span>Download Template</span>
                      </button>
                    </div>
                  </div>

                  {/* File Upload */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                    <SafeIcon icon={FiUpload} className="mx-auto text-4xl text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-800 mb-2">Select Template File</h3>
                    <p className="text-gray-600 mb-4">
                      Drag and drop your JSON template file here, or click to browse
                    </p>
                    <label className="inline-block">
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <span className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors cursor-pointer">
                        Choose Excel/CSV File
                      </span>
                    </label>
                    {file && (
                      <p className="text-sm text-green-600 mt-2">
                        Selected: {file.name}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mt-3">
                      Only Excel (.xlsx, .xls) and CSV files are supported
                    </p>
                  </div>

                  {/* Preview Data */}
                  {previewData && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-800 mb-3">Preview (First 3 rows)</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="bg-gray-200">
                              <th className="px-2 py-1 text-left">Text</th>
                              <th className="px-2 py-1 text-left">Type</th>
                              <th className="px-2 py-1 text-left">Subject</th>
                              <th className="px-2 py-1 text-left">Course Code</th>
                              <th className="px-2 py-1 text-left">Difficulty</th>
                              <th className="px-2 py-1 text-left">CEFR Level</th>
                              <th className="px-2 py-1 text-left">Marks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.map((row, index) => (
                              <tr key={index} className="border-t">
                                <td className="px-2 py-1 max-w-xs truncate">{row.text}</td>
                                <td className="px-2 py-1">{row.type}</td>
                                <td className="px-2 py-1">{row.subject}</td>
                                <td className="px-2 py-1 font-mono text-blue-600">{row.courseCode}</td>
                                <td className="px-2 py-1">{row.difficulty}</td>
                                <td className="px-2 py-1">{row.cefrLevel}</td>
                                <td className="px-2 py-1">{row.marks}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Upload Results */}
                  {uploadResults && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-800 mb-3">Upload Results</h4>
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">{uploadResults.total}</div>
                          <div className="text-sm text-gray-600">Total Rows</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">{uploadResults.successful}</div>
                          <div className="text-sm text-gray-600">Successful</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-red-600">{uploadResults.failed}</div>
                          <div className="text-sm text-gray-600">Failed</div>
                        </div>
                      </div>

                      {uploadResults.errors.length > 0 && (
                        <div>
                          <h5 className="font-medium text-red-800 mb-2">Errors:</h5>
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {uploadResults.errors.map((error, index) => (
                              <div key={index} className="text-sm text-red-700 bg-red-50 p-2 rounded">
                                Row {error.row}: {error.errors.join(', ')}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                    <button
                      onClick={handleClose}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      Cancel
                    </button>
                    <motion.button
                      onClick={handleUpload}
                      disabled={!file || isUploading}
                      className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                      whileHover={{ scale: !file || isUploading ? 1 : 1.02 }}
                      whileTap={{ scale: !file || isUploading ? 1 : 0.98 }}
                    >
                      {isUploading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Uploading...</span>
                        </>
                      ) : (
                        <>
                          <SafeIcon icon={FiUpload} />
                          <span>Upload Questions</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                </div>
              ) : (
                /* Instructions Tab */
                <div className="space-y-6">
                  {/* Overview */}
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
                    <h4 className="font-bold text-blue-800 mb-3 flex items-center">
                      <SafeIcon icon={FiInfo} className="mr-2" />
                      Excel File Format Instructions
                    </h4>
                    <p className="text-blue-700 mb-4">
                      Your Excel file should have column headers in the first row, with each subsequent row containing question data. 
                      <strong> Course Code and CEFR Level are REQUIRED</strong> for proper question tagging and template generation.
                    </p>
                    <div className="bg-white rounded-lg p-4 border border-blue-300">
                      <p className="text-sm font-medium text-gray-800 mb-2">Supported File Types:</p>
                      <div className="flex space-x-4 text-sm">
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded">.xlsx</span>
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded">.xls</span>
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded">.csv</span>
                      </div>
                    </div>
                  </div>

                  {/* Required Columns */}
                  <div className="bg-white border border-red-200 rounded-lg p-6">
                    <h4 className="font-bold text-red-800 mb-4 flex items-center">
                      <SafeIcon icon={FiAlertTriangle} className="mr-2" />
                      Required Columns (Must Include All)
                    </h4>
                    <div className="grid gap-4">
                      {requiredColumns.map((col, index) => (
                        <div key={index} className="border-l-4 border-red-400 pl-4">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-mono text-sm bg-red-100 text-red-800 px-2 py-1 rounded">
                              {col.name}
                            </span>
                            <span className="text-xs text-red-600 font-medium">REQUIRED</span>
                          </div>
                          <p className="text-sm text-gray-700 mb-1">{col.description}</p>
                          <p className="text-xs text-gray-600 italic">Example: {col.example}</p>
                          {col.name === 'courseCode' && (
                            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                              <p className="text-xs text-yellow-800 font-medium">
                                ⚠️ NEW REQUIREMENT: Course codes enable proper question tagging and template auto-population
                              </p>
                            </div>
                          )}
                          {col.name === 'cefrLevel' && (
                            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                              <p className="text-xs text-yellow-800 font-medium">
                                ⚠️ NEW REQUIREMENT: CEFR level is required for language proficiency categorization
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Optional Columns */}
                  <div className="bg-white border border-blue-200 rounded-lg p-6">
                    <h4 className="font-bold text-blue-800 mb-4 flex items-center">
                      <SafeIcon icon={FiLayers} className="mr-2" />
                      Optional Columns
                    </h4>
                    <div className="grid gap-4">
                      {optionalColumns.map((col, index) => (
                        <div key={index} className="border-l-4 border-blue-400 pl-4">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-mono text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              {col.name}
                            </span>
                            <span className="text-xs text-blue-600 font-medium">OPTIONAL</span>
                          </div>
                          <p className="text-sm text-gray-700 mb-1">{col.description}</p>
                          <p className="text-xs text-gray-600 italic">Example: {col.example}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Validation Rules */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <h4 className="font-bold text-yellow-800 mb-4 flex items-center">
                      <SafeIcon icon={FiCheckCircle} className="mr-2" />
                      Validation Rules
                    </h4>
                    <div className="space-y-3 text-sm">
                      <div className="grid gap-2">
                        <p><strong>Question Types:</strong> Multiple Choice, Short Answer, Essay, True/False, Fill in the Blank, Problem Solving</p>
                        <p><strong>Difficulty Levels:</strong> Easy, Medium, Hard</p>
                        <p><strong>Bloom's Levels:</strong> knowledge, comprehension, application, analysis, synthesis, evaluation</p>
                        <p><strong>Vocabulary Tiers:</strong> A1, A2, B1, B2, C1, C2</p>
                        <p><strong>CEFR Levels:</strong> A1, A2, B1, B2, C1, C2</p>
                        <p><strong>Course Code Format:</strong> 2-4 letters followed by 3-4 numbers (e.g., CS101, MATH2001, LLE3012)</p>
                        <p><strong>Marks:</strong> Must be a positive number (1, 2, 5, 10, etc.)</p>
                        <p><strong>Multiple Choice:</strong> Must have at least 2 options, and correct answer must match one of the options</p>
                      </div>
                    </div>
                  </div>

                  {/* CEFR Level Guide */}
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
                    <h4 className="font-bold text-indigo-800 mb-4 flex items-center">
                      <SafeIcon icon={FiInfo} className="mr-2" />
                      CEFR Level Guide
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white p-3 rounded border border-indigo-100">
                        <h5 className="text-sm font-medium text-indigo-800">A1-A2: Basic Users</h5>
                        <p className="text-xs text-gray-600">
                          Simple vocabulary and grammar for everyday situations. Basic questions and statements.
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded border border-indigo-100">
                        <h5 className="text-sm font-medium text-indigo-800">B1-B2: Independent Users</h5>
                        <p className="text-xs text-gray-600">
                          More complex language for expressing opinions and explaining. Can discuss familiar topics.
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded border border-indigo-100">
                        <h5 className="text-sm font-medium text-indigo-800">C1-C2: Proficient Users</h5>
                        <p className="text-xs text-gray-600">
                          Advanced vocabulary and complex structures for academic/professional contexts. Abstract concepts.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Example Table */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="font-bold text-gray-800 mb-4 flex items-center">
                      <SafeIcon icon={FiFileText} className="mr-2" />
                      Example Excel Layout
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs border border-gray-300">
                        <thead>
                          <tr className="bg-gray-200">
                            <th className="border border-gray-300 px-2 py-1 text-left">text</th>
                            <th className="border border-gray-300 px-2 py-1 text-left">type</th>
                            <th className="border border-gray-300 px-2 py-1 text-left">subject</th>
                            <th className="border border-gray-300 px-2 py-1 text-left bg-yellow-100">courseCode</th>
                            <th className="border border-gray-300 px-2 py-1 text-left">difficulty</th>
                            <th className="border border-gray-300 px-2 py-1 text-left">bloomLevel</th>
                            <th className="border border-gray-300 px-2 py-1 text-left">vocabularyTier</th>
                            <th className="border border-gray-300 px-2 py-1 text-left bg-indigo-100">cefrLevel</th>
                            <th className="border border-gray-300 px-2 py-1 text-left">marks</th>
                            <th className="border border-gray-300 px-2 py-1 text-left">options</th>
                            <th className="border border-gray-300 px-2 py-1 text-left">correctAnswer</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border border-gray-300 px-2 py-1">What is the capital of France?</td>
                            <td className="border border-gray-300 px-2 py-1">Multiple Choice</td>
                            <td className="border border-gray-300 px-2 py-1">Geography</td>
                            <td className="border border-gray-300 px-2 py-1 bg-yellow-50 font-mono">GEO101</td>
                            <td className="border border-gray-300 px-2 py-1">Easy</td>
                            <td className="border border-gray-300 px-2 py-1">knowledge</td>
                            <td className="border border-gray-300 px-2 py-1">A1</td>
                            <td className="border border-gray-300 px-2 py-1 bg-indigo-50 font-mono">A1</td>
                            <td className="border border-gray-300 px-2 py-1">1</td>
                            <td className="border border-gray-300 px-2 py-1">Paris, London, Berlin, Madrid</td>
                            <td className="border border-gray-300 px-2 py-1">Paris</td>
                          </tr>
                          <tr>
                            <td className="border border-gray-300 px-2 py-1">Explain photosynthesis</td>
                            <td className="border border-gray-300 px-2 py-1">Short Answer</td>
                            <td className="border border-gray-300 px-2 py-1">Biology</td>
                            <td className="border border-gray-300 px-2 py-1 bg-yellow-50 font-mono">BIO101</td>
                            <td className="border border-gray-300 px-2 py-1">Medium</td>
                            <td className="border border-gray-300 px-2 py-1">comprehension</td>
                            <td className="border border-gray-300 px-2 py-1">B1</td>
                            <td className="border border-gray-300 px-2 py-1 bg-indigo-50 font-mono">B1</td>
                            <td className="border border-gray-300 px-2 py-1">5</td>
                            <td className="border border-gray-300 px-2 py-1"></td>
                            <td className="border border-gray-300 px-2 py-1"></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Tips */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h4 className="font-bold text-green-800 mb-4">📝 Pro Tips</h4>
                    <ul className="space-y-2 text-sm text-green-700">
                      <li>• Use the first row for column headers exactly as specified</li>
                      <li>• <strong>Course Code is mandatory</strong> - format like CS101, MATH201, LLE3012</li>
                      <li>• <strong>CEFR Level is mandatory</strong> - use A1, A2, B1, B2, C1, or C2</li>
                      <li>• Don't leave empty rows between questions</li>
                      <li>• For Multiple Choice questions, separate options with commas</li>
                      <li>• Make sure the correct answer exactly matches one of the options</li>
                      <li>• Use consistent spelling for difficulty levels and question types</li>
                      <li>• Download our template for a perfect starting point</li>
                      <li>• Test with a small file first (2-3 questions) to verify format</li>
                      <li>• Questions will be automatically tagged with their course codes for template generation</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ExcelUploadModal;